import Table from "@/components/Table/table";
import { useEffect, useMemo, useState } from "react";
import {
  MotifButton,
  MotifIcon,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
  MotifProgressLoader,
} from "@ey-xd/motif-react";
import { useCase } from "@/features/case/use-case";
import {
  useDeletePayeeTypeMutation,
  useGetPayeeTypeApiDataQuery,
} from "@/features/distributions/steps/calculationStepReducer";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import { IconoirWarningTriangle } from "@ey-xd/motif-icon";
import { useNavigate, useParams, Link } from "react-router-dom";
import CustomPagination from "@/components/Pagination/CustomPagination";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { havingAddPermission, havingDeletePermission } from "@/utility/common";
import {
  CANCEL_BUTTON_VARIANT,
  CREATE_BUTTON_VARIANT,
  GetCommonDeleteImgButton,
  YES_BUTTON_VARIANT,
} from "@/components/Images/CommonImages";
import { useTrans } from "@/components/Functions/dateFunc";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";

export const PayeeType = () => {
  const caseData = useCase();
  const navigate = useNavigate();
  const { caseId } = useParams();
  const permissions = useSelector((state: any) => state.permissions);
  const { PayeeType } = permissions;
  const [isLoading, setIsLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState<any>(null);
  const [deleteSuccessMsg, setDeleteSuccessMsg] = useState<any>(null);
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [editId, setEditId] = useState<any>("");
  const [deleteEntityName, setDeleteEntityName] = useState<any>("");
  const [showModal, setShowModal] = useState<any>(null);
  const [deletePayeeType] = useDeletePayeeTypeMutation();
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  let tableLoadingData = customTableLoadingData;
  const retainState = useSelector(
    (e: any) => e.retainState.payeeTypeListTabState
  );
  const dispatch = useDispatch();
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      CaseId: caseData.caseId,
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize || paginationPageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  }, [caseData?.caseId, paginationPageSize, retainState]);
  const paginationObj = getPaginationObj;
  const {
    data,
    isLoading: isPayeeTypeDataLoading,
    refetch,
    isFetching,
    error,
    status,
  } = useGetPayeeTypeApiDataQuery(paginationObj);

  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "payeeTypeListTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }

  async function deletePayee() {
    setStatusMsg(null);
    setDeleteSuccessMsg(null);
    setIsLoading(true);
    await deletePayeeType(editId).then((response: any) => {
      if (response.data) {
        setDeleteSuccessMsg(
          t("pages.distributions.payeeType.PayeeDeletedMessage")
        );
        refetch();
      } else {
        const errorMessage = response?.error?.data?.message;
        errorMessage
          ? setStatusMsg(errorMessage)
          : setStatusMsg(t("global.globalNotifications.unExpectedError"));
      }
      setIsLoading(false);
      setShowModal(false);
    });
  }

  const caseRenderers = {
    actionCellRenderer: function (params: any) {
      return (
        <>
          {havingDeletePermission(PayeeType) && (
            <GetCommonDeleteImgButton
              onClick={() => {
                setEditId(params.data.id);
                setShowModal(true);
                setDeleteEntityName(params.data.name);
              }}
            ></GetCommonDeleteImgButton>
          )}
          {/* {havingEditPermission(PayeeType) &&<MotifButton
            size="small"
            type="submit"
            variant="primary"
            onClick={() => {
              setEditId(params.data.id);
              navigate(`/case/${caseId}/configuration/payee/edit/${params.data.id}`);
            }}
          >
            <MotifIcon
              iconFunction={IconoirEditPencil}
              stroke="blue"
              strokeWidth="2"
              size="24"
            />
          </MotifButton>
    } */}
        </>
      );
    },
    // descriptionCellRenderer: function (params: any) {
    //   const description = params?.data?.description || "";
    //   return <TextWithTooltip text={description} />;
    // },
  };
  const columnDefs = [
    {
      cellRenderer: (params: any) => (
        <Link to={`./configuration/payee/edit/${params?.data?.id}`}>
          <span className="text-bold-underline">{params?.data?.name}</span>
        </Link>
      ),
      comparator: () => 0,
      field: "name",
      type: "nameType",
      headerName: t("pages.distributions.payeeType.columnDetails.column1"),
      sortable: true,
      unSortIcon: true,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
      },
      autoHeight: true,
      flex: 1,
      minWidth: 180,
    },
    {
      comparator: () => 0,
      field: "payeeLevel",
      type: "payeeLevelType",
      headerName: t("pages.distributions.payeeType.columnDetails.column2"),
      sortable: true,
      unSortIcon: true,
      flex: 1,
      minWidth: 180,

      cellRenderer: function (params: any) {
        return (
          <>{params?.data.payeeLevel === 0 ? "Creditor Level" : "Case Level"}</>
        );
      },
    },
    {
      cellRenderer: "descriptionCellRenderer",
      comparator: () => 0,
      field: "description",
      type: "descriptionType",
      headerName: t("pages.distributions.payeeType.columnDetails.column3"),
      sortable: true,
      unSortIcon: true,
      flex: 1,
      minWidth: 220,
      wrapText: true,
      autoHeight: true,
      cellClass: "overFlowWrapAnywhere",
    },
    {
      cellRenderer: "actionCellRenderer",
      field: "action",
      headerName: "",
      sortable: false,
      cellStyle: {
        "justify-content": "right",
      },
    },
  ];
  function TitleComplement() {
    const { t } = useTranslation();
    const handleCreateNew = () => {
      navigate(`/case/${caseId}/configuration/payee/create`);
    };
    return havingAddPermission(PayeeType) ? (
      <MotifButton variant={CREATE_BUTTON_VARIANT} onClick={handleCreateNew}>
        {t("pages.distributions.payeeType.createNewPayeeType")}
      </MotifButton>
    ) : (
      <></>
    );
  }
  return (
    <>
      <div className="stage-management-container">
        <div className="motif-body1-default-regular stage-management-title mar-b-20">
          {t("pages.distributions.payeeType.title")}{" "}
        </div>
        <MotifModal
          onClose={() => setShowModal(false)}
          show={showModal}
          focusTrapOptions={{
            tabbableOptions: {
              displayCheck: "none",
            },
          }}
        >
          <div className="confirmationModalWrapper creditor_disabling_model">
            <MotifModalHeader
              headerIcon={
                <>
                  <span
                    style={{
                      backgroundColor: "rgb(252, 245, 243)",
                      borderRadius: "50px",
                      padding: "5px 8px 2px 8px",
                    }}
                  >
                    <MotifIcon
                      iconFunction={IconoirWarningTriangle}
                      strokeWidth="1"
                      size="15"
                      stroke="red"
                    />{" "}
                  </span>
                  <span
                    style={{
                      fontSize: 16,
                      display: "block",
                      width: "100%",
                      marginLeft: "10px",
                    }}
                  >
                    {t("global.globalNotifications.warning")}
                  </span>
                </>
              }
            ></MotifModalHeader>

            <MotifModalBody style={{ paddingTop: "0px" }}>
              <span style={{ fontWeight: "300" }}>
                {useTrans(
                  t,
                  t(
                    "pages.distributions.payeeType.validationMessages.deleteWarningMessage",
                    {
                      deleteEntityName: deleteEntityName,
                    }
                  ),
                  "deleteEntityName"
                )}
              </span>
            </MotifModalBody>
            <MotifModalFooter>
              <MotifButton
                size="small"
                type={isLoading ? "loader" : "submit"}
                variant={YES_BUTTON_VARIANT}
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
                onClick={() => {
                  deletePayee();
                }}
              >
                {t("global.globalActions.yes")}
              </MotifButton>
              <MotifButton
                className=""
                onClick={() => setShowModal(false)}
                size="small"
                type="button"
                variant={CANCEL_BUTTON_VARIANT}
              >
                {t("global.globalActions.cancel")}
              </MotifButton>
            </MotifModalFooter>
          </div>
        </MotifModal>
        {statusMsg && (
          <div className="mar-b-20">
            <NotificationGrid show={true} message={statusMsg} type="error" />
          </div>
        )}
        {deleteSuccessMsg && (
          <div className="mar-b-20">
            <NotificationGrid
              show={true}
              message={deleteSuccessMsg}
              type="success"
            />
          </div>
        )}
        {isPayeeTypeDataLoading ? (
          <MotifProgressLoader show variant="default" />
        ) : (
          <>
            <Table
              columnDefs={columnDefs}
              columnTypes={{ ...retainState.sortedColumnTypes }}
              handlePaginationData={handlePaginationData}
              paginationObj={paginationObj}
              cellRenderers={isFetching ? [] : data?.results || []}
              TitleComplement={TitleComplement}
              title={"pages.distributions.payeeType.tableTitle"}
              description={"pages.distributions.payeeType.tableDescription"}
              pagination={false}
              caseRenderers={caseRenderers}
              overlayNoRowsTemplate={
                isFetching
                  ? t(tableLoadingData)
                  : error
                  ? "Something Went Wrong"
                  : "No records found"
              }
            />
            <CustomPagination
               apiData={data}
               apiStatus={status}
              currentpage={paginationObj.PageNumber}
              pageSize={paginationObj.PageSize}
              disableNext={!data?.hasMoreRecords}
              onChangePageSize={(page: number) => {
                dispatch(
                  setComponentState1({
                    entityName: "payeeTypeListTabState",
                    entityState: {
                      PageSize: page,
                      PageNumber: 1,
                    },
                  })
                );
              }}
              onChangeNextPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "payeeTypeListTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber + 1,
                    },
                  })
                );
              }}
              onChangePreviousPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "payeeTypeListTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber - 1,
                    },
                  })
                );
              }}
              pageSizeSelector={paginationPageSizeSelector}
            />
          </>
        )}
      </div>
    </>
  );
};
